import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ApiResult } from '../auth/api-client';
import type { KitStatus, KitSummary, KitTemplate } from '../shared/types';
import { useKitTemplatesApi } from './kit-templates-api';
import { summarizeKitListOutput } from './kit-ui';
import { useKitsApi } from './kits-api';

const STATUS_FILTERS: readonly (KitStatus | 'ALL')[] = ['DRAFT', 'COMPLETE', 'CANCELED', 'ALL'];
export const DEFAULT_KIT_STATUS_FILTER: KitStatus | 'ALL' = 'COMPLETE';

function failMessage(result: { ok: false; error: string; message?: string }): string {
  return result.message ?? result.error;
}

function formatDate(value: string | undefined): string {
  if (value === undefined) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export function KitsListPage(): JSX.Element {
  const api = useKitsApi();
  const templatesApi = useKitTemplatesApi();
  const navigate = useNavigate();
  const [status, setStatus] = useState<KitStatus | 'ALL'>(DEFAULT_KIT_STATUS_FILTER);
  const [kits, setKits] = useState<KitSummary[]>([]);
  const [templates, setTemplates] = useState<readonly KitTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [loading, setLoading] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingKitId, setDeletingKitId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await api.list(status === 'ALL' ? {} : { status });
    setLoading(false);
    if (!res.ok) {
      setError(failMessage(res));
      return;
    }
    setKits([...res.data.kits]);
  }, [api, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setError(null);
    const res = await templatesApi.list();
    setTemplatesLoading(false);
    if (!res.ok) {
      setError(failMessage(res));
      return;
    }
    const active = [...res.data.templates]
      .filter((template) => template.status === 'ACTIVE')
      .sort(compareTemplatesForCreate);
    setTemplates(active);
    setSelectedTemplateId((current) => {
      if (current !== '' && active.some((template) => template.templateId === current)) {
        return current;
      }
      return active.find((template) => template.isDefault)?.templateId ?? active[0]?.templateId ?? '';
    });
  }, [templatesApi]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const onCreate = useCallback(async () => {
    setCreating(true);
    setError(null);
    const body = selectedTemplateId === '' ? {} : { templateId: selectedTemplateId };
    const res: ApiResult<{ kitId: string }> = await api.create(body);
    setCreating(false);
    if (!res.ok) {
      setError(failMessage(res));
      return;
    }
    navigate(`/kits/${encodeURIComponent(res.data.kitId)}`);
  }, [api, navigate, selectedTemplateId]);

  const onDelete = useCallback(
    async (kit: KitSummary) => {
      const label = kit.title ?? kit.kitId;
      if (!window.confirm(`Delete Kit "${label}"? This cannot be undone.`)) return;
      setDeletingKitId(kit.kitId);
      setError(null);
      const res = await api.deleteKit(kit.kitId);
      setDeletingKitId(null);
      if (!res.ok) {
        setError(failMessage(res));
        return;
      }
      setKits((current) => current.filter((candidate) => candidate.kitId !== kit.kitId));
    },
    [api],
  );

  const createDisabled = creating || templatesLoading || templates.length === 0;

  return (
    <div>
      <h2>Kits</h2>
      <div className="tcvs-toolbar">
        <label className="tcvs-field">
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value as KitStatus | 'ALL')}>
            {STATUS_FILTERS.map((value) => (
              <option key={value} value={value}>
                {value === 'ALL' ? 'All' : value}
              </option>
            ))}
          </select>
        </label>
        <button type="button" onClick={() => void load()} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
        <label className="tcvs-field">
          <span>Template</span>
          <select
            value={selectedTemplateId}
            disabled={templatesLoading || creating || templates.length === 0}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
          >
            {templates.length === 0 ? <option value="">No active templates</option> : null}
            {templates.map((template) => (
              <option key={template.templateId} value={template.templateId}>
                {templateOptionLabel(template)}
              </option>
            ))}
          </select>
        </label>
        <button type="button" onClick={() => void loadTemplates()} disabled={templatesLoading}>
          {templatesLoading ? 'Loading templates...' : 'Refresh templates'}
        </button>
        <button type="button" onClick={() => void onCreate()} disabled={createDisabled}>
          {creating ? 'Creating...' : 'Create Kit'}
        </button>
      </div>

      {error !== null ? (
        <p className="tcvs-error-text" role="alert">
          {error}
        </p>
      ) : null}

      <section className="tcvs-card">
        {kits.length === 0 && !loading ? <p className="tcvs-muted">No Kits found.</p> : null}
        {kits.length > 0 ? (
          <div className="tcvs-table-wrap">
            <table className="tcvs-table">
              <thead>
                <tr>
                  <th>Kit</th>
                  <th>Status</th>
                  <th>Cards</th>
                  <th>Output</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {kits.map((kit) => (
                  <tr key={kit.kitId}>
                    <td>
                      <Link to={`/kits/${encodeURIComponent(kit.kitId)}`}>
                        {kit.title ?? kit.kitId}
                      </Link>
                      {kit.title !== undefined ? (
                        <div className="tcvs-caption tcvs-mono">{kit.kitId}</div>
                      ) : null}
                    </td>
                    <td>{kit.status}</td>
                    <td>{kit.itemCount}</td>
                    <td>{summarizeKitListOutput(kit)}</td>
                    <td>{formatDate(kit.updatedAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="tcvs-btn-secondary"
                        disabled={deletingKitId === kit.kitId}
                        onClick={() => void onDelete(kit)}
                      >
                        {deletingKitId === kit.kitId ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function compareTemplatesForCreate(a: KitTemplate, b: KitTemplate): number {
  if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
  if (a.cardCount !== b.cardCount) return a.cardCount - b.cardCount;
  if (a.source !== b.source) return a.source === 'operator' ? -1 : 1;
  return a.name.localeCompare(b.name);
}

function templateOptionLabel(template: KitTemplate): string {
  const marker = template.isDefault ? ' default' : '';
  return `${template.cardCount}-card - ${template.name}${marker}`;
}
